import { setOpacity } from "@memorang/helpers/setOpacity";
import Icon from "@react-native-vector-icons/material-design-icons/static";
import { Image } from "expo-image";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ImageSourcePropType } from "react-native";
import { Pressable } from "react-native";
import { Menu, ProgressBar, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import Box from "../../../Box";
import ToeicProgressBar from "../../../ToeicDriver/ToeicProgressBar";
import ProgressTimer from "../../../ToeicDriver/ToeicProgressBar/ProgressTimer";
import type { ToeicProgressBarProps } from "../../../ToeicDriver/ToeicProgressBar/types";

export type ToeicSummativeAppbarLogo = {
	light: ImageSourcePropType;
	dark: ImageSourcePropType;
};

type Props = {
	logo: ToeicSummativeAppbarLogo;
	logoWidth?: number;
	logoHeight?: number;
	testName: string;
	section: string;
	testNameLabel?: string;
	sectionLabel?: string;
	progressBar?: ToeicProgressBarProps;
	onQuit?: () => void;
	onExit?: () => void;
};

type SessionMenuProps = {
	onQuit?: () => void;
	onExit?: () => void;
	iconSize?: number;
};

const SessionMenu = ({ onQuit, onExit, iconSize = 20 }: SessionMenuProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const [menuVisible, setMenuVisible] = useState(false);

	if (!(onQuit || onExit)) {
		return null;
	}

	const runAction = (action: () => void) => () => {
		setMenuVisible(false);
		action();
	};

	return (
		<Menu
			visible={menuVisible}
			onDismiss={() => setMenuVisible(false)}
			anchor={
				<Pressable
					onPress={() => setMenuVisible(true)}
					accessibilityRole="button"
					accessibilityLabel={t("testDriver.appBar.menuLabel")}
					hitSlop={8}
				>
					<Icon
						name="dots-vertical"
						size={iconSize}
						color={theme.colors.onPrimary}
					/>
				</Pressable>
			}
		>
			{onQuit ? (
				<Menu.Item
					leadingIcon="pause-circle-outline"
					title={t("testDriver.appBar.pause")}
					onPress={runAction(onQuit)}
				/>
			) : null}
			{onExit ? (
				<Menu.Item
					leadingIcon="logout"
					title={t("session.navigation.exit")}
					onPress={runAction(onExit)}
				/>
			) : null}
		</Menu>
	);
};

type MobileAppbarProps = {
	title: string;
	progressBar?: ToeicProgressBarProps;
	onQuit?: () => void;
	onExit?: () => void;
};

const ToeicMobileAppbar = ({
	title,
	progressBar,
	onQuit,
	onExit,
}: MobileAppbarProps) => {
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();
	const showTimer = progressBar ? (progressBar.showTimer ?? true) : false;
	const questionProgress = progressBar
		? Math.max(
				0,
				Math.min(
					1,
					progressBar.currentQuestion / Math.max(1, progressBar.totalQuestions),
				),
			)
		: 0;

	return (
		<Box backgroundColor={theme.colors.primary} paddingTop={insets.top}>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				padding={16}
				gap={16}
			>
				<Text
					numberOfLines={1}
					variant="titleSmall"
					style={{
						color: theme.colors.onPrimary,
						fontWeight: "600",
						flexShrink: 1,
					}}
				>
					{title}
				</Text>
				<Box flexDirection="row" alignItems="center" gap={16}>
					{progressBar && showTimer ? (
						<ProgressTimer
							label=""
							timeLeftSeconds={progressBar.timeLeftSeconds}
							color={theme.colors.onPrimary}
						/>
					) : null}
					<SessionMenu onQuit={onQuit} onExit={onExit} />
				</Box>
			</Box>
			{progressBar ? (
				<ProgressBar
					progress={questionProgress}
					color={theme.colors.onPrimary}
					style={{
						height: 4,
						backgroundColor: setOpacity(theme.colors.onPrimary, 0.3),
					}}
				/>
			) : null}
		</Box>
	);
};

const ToeicSummativeAppbar = ({
	logo,
	logoWidth,
	logoHeight,
	testName,
	section,
	testNameLabel,
	sectionLabel,
	progressBar,
	onQuit,
	onExit,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	const insets = useSafeAreaInsets();
	const source = theme.dark ? logo.light : logo.dark;
	const resolvedTestNameLabel =
		testNameLabel ?? t("testDriver.appBar.testNameLabel");
	const resolvedSectionLabel =
		sectionLabel ?? t("testDriver.appBar.sectionLabel");
	const resolvedLogoWidth = logoWidth ?? 146;
	const resolvedLogoHeight = logoHeight ?? 26;

	if (isMobile) {
		return (
			<ToeicMobileAppbar
				title={testName || section}
				progressBar={progressBar}
				onQuit={onQuit}
				onExit={onExit}
			/>
		);
	}

	return (
		<Box backgroundColor={theme.colors.primary} paddingTop={insets.top}>
			<Box
				flexDirection="row"
				alignItems="center"
				paddingHorizontal={24}
				paddingVertical={16}
				gap={12}
			>
				<Box flex={1}>
					<Image
						source={source}
						style={{ width: resolvedLogoWidth, height: resolvedLogoHeight }}
						contentFit="contain"
						contentPosition="left"
						alt={t("testDriver.appBar.logoAlt")}
					/>
				</Box>
				<Box alignItems="flex-end" gap={2}>
					<Box flexDirection="row" gap={6}>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.surfaceVariant }}
						>
							{resolvedTestNameLabel}:
						</Text>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onPrimary, fontWeight: "700" }}
						>
							{testName}
						</Text>
					</Box>
					<Box flexDirection="row" gap={6}>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.surfaceVariant }}
						>
							{resolvedSectionLabel}:
						</Text>
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onPrimary, fontWeight: "700" }}
						>
							{section}
						</Text>
					</Box>
				</Box>
				<SessionMenu onQuit={onQuit} onExit={onExit} iconSize={24} />
			</Box>
			{progressBar && <ToeicProgressBar {...progressBar} />}
		</Box>
	);
};

export default ToeicSummativeAppbar;
